/**
 * GenericConnector — self-service REST / Webhook adapter (INT-7).
 *
 * Each generic connector carries its own endpoint config; no shared env key.
 * Mock mode is toggled per-connector via `config.mockMode` (defaulted true so
 * a new generic connector is demo-safe out of the box).
 */

import IntegrationService from '../IntegrationService';
import DataService from '../DataService';
import AegisFieldMappingService from '../AegisFieldMappingService';

function isGenericMockMode(connector) {
  if (connector?.config?.mockMode !== undefined) return !!connector.config.mockMode;
  return true;
}

export function webhookUrlFor(connector) {
  // Cloud Function URL pattern — same region as the rest of the app.
  const base = (typeof window !== 'undefined' && window.__WATCHMEN_FUNCTIONS_BASE__) || 'https://us-central1-watchmen-tms.cloudfunctions.net';
  return `${base}/inboundWebhook?connectorId=${connector.id}&tenantId=${connector.tenantId}`;
}

async function sync(connector) {
  if (isGenericMockMode(connector)) {
    return {
      recordsIn: 0,
      recordsOut: 0,
      errors: 0,
      status: 'success',
      summary: 'Mock generic connector — configure apiBase + auth to enable live polling',
    };
  }

  // Live polling — minimal implementation. Users configure the endpoint in
  // the setup dialog. The poller fetches and appends to connector_webhook_events
  // with the raw payload for downstream mapping.
  const { apiBase, authType } = connector.config || {};
  if (!apiBase) {
    return { recordsIn: 0, recordsOut: 0, errors: 1, status: 'failed', summary: 'Missing apiBase on generic connector config' };
  }

  try {
    const res = await fetch(apiBase, { method: 'GET', headers: authType === 'bearer' ? { Authorization: `Bearer ${connector.config.apiKeyRef || ''}` } : {} });
    if (!res.ok) return { recordsIn: 0, recordsOut: 0, errors: 1, status: 'failed', summary: `HTTP ${res.status}` };
    const payload = await res.json();
    const rows = Array.isArray(payload) ? payload : [payload];
    for (const row of rows) {
      await DataService.create('connector_webhook_events', {
        tenantId: connector.tenantId,
        connectorId: connector.id,
        direction: 'inbound',
        payload: row,
        receivedAt: new Date().toISOString(),
      }, 'generic_connector_poll', 'generic_rest');
    }
    return { recordsIn: rows.length, recordsOut: 0, errors: 0, status: 'success', summary: `Polled ${rows.length} records` };
  } catch (e) {
    return { recordsIn: 0, recordsOut: 0, errors: 1, status: 'failed', summary: e.message };
  }
}

async function healthCheck(connector) {
  if (isGenericMockMode(connector)) return { health: 'green', error: null };
  const { apiBase } = connector.config || {};
  if (!apiBase) return { health: 'yellow', error: 'Missing apiBase' };
  try {
    const res = await fetch(apiBase);
    return { health: res.ok ? 'green' : 'yellow', error: res.ok ? null : `HTTP ${res.status}` };
  } catch (e) {
    return { health: 'red', error: e.message };
  }
}

IntegrationService.register('generic_rest', {
  label: 'Generic REST / Webhook',
  category: 'generic',
  description: 'Self-service connector for any REST API — AEGIS field mapping',
  icon: '🔌',
  envKey: null, // Per-connector credentials, not global
  setupComponent: 'GenericConnectorSetup',
  defaultInterval: 30,
  directions: ['inbound', 'outbound'],
  capabilities: ['poll', 'webhook'],
  sync,
  healthCheck,
  describeSchema: () => ([]), // Known at setup time from user-supplied sample
});

export default {
  connectorType: 'generic_rest',
  webhookUrlFor,
  suggestMapping: (sample) => AegisFieldMappingService.suggestFromSample(sample),
};
